import { useEffect, useMemo, useState } from 'react';

import { toZonedTime } from 'date-fns-tz';
import { addYears, subYears } from 'date-fns';

import { cn } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { parseDateOnly } from 'lib/helpers/dateUtils';

import Popover from 'components/shared/Popovers/Popover/Popover';

import Header from '../shared/Header';
import Calendar from '../Calendar/Calendar';

import Footer from './components/Footer';
import OuterButton from './components/OuterButton';
import { getRangeError } from './lib/getRangeError';
import { combineDateTime } from './lib/combineDateTime';
import DateSelector from './components/DateSelector/DateSelector';
import TimeSelector from './components/TimeSelector/TimeSelector';
import { getButtonText, getDisplayFormat } from './lib/getDateFormats';
import { extractDateValue, extractTimeValue } from './lib/extractValues';

// Viewport heights below which the panel cannot sit next to its trigger without being clipped: the
// fields, calendar, optional time row and footer come to roughly 590px / 530px plus breathing room.
// Below these it is centred like a dialog instead. Kept as named constants rather than inline pixel
// values so the two variants stay visibly related.
const CENTER_WHEN_SHORT_WITH_TIME =
  '[@media(max-height:700px)]:!top-1/2 [@media(max-height:700px)]:!left-1/2 [@media(max-height:700px)]:-translate-x-1/2 [@media(max-height:700px)]:-translate-y-1/2';
// Tightened spacing that pairs with the centring breakpoints below.
const COMPACT_WHEN_SHORT = '[@media(max-height:700px)]:gap-3 [@media(max-height:700px)]:p-4';
const CENTER_WHEN_SHORT_DATE_ONLY =
  '[@media(max-height:620px)]:!top-1/2 [@media(max-height:620px)]:!left-1/2 [@media(max-height:620px)]:-translate-x-1/2 [@media(max-height:620px)]:-translate-y-1/2';

const toDateOnlyString = (date) => {
  if (!date) return null;
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
};

/**
 * @param {import('./lib/propTypes').DatePickerProps} props
 */

const Datepicker = (props) => {
  const {
    value = null,
    onChange = () => {},
    format = 'MM/DD/YYYY',
    isDate = true,
    isDateOnly = false,
    isDateRange = false,
    isTime = false,
    isTimeRange = false,
    isTimezone: isTimezoneProp,
    buttonIcon = 'calendar-mono',
    buttonIconSize = 20,
    buttonIconClassName,
    buttonText,
    buttonTextClassName = '',
    buttonClassName = '',
    parentClassName = '',
    panelClassName = '',
    label = '',
    required = false,
    error = null,
    customButtonTrigger = null,
    showTimezone = true,
    minDate = subYears(new Date(), 150),
    maxDate = addYears(new Date(), 150),
    firstDayOfWeek = 0,
    timezone: timezoneProp = null
  } = props;

  // The hook chain (practice atom + user preferences) doesn't exist in anonymous flows
  // (e.g. self-scheduling), where it silently falls back to America/New_York — callers
  // there pass the practice timezone explicitly instead.
  const userTimezone = useTimezone({ enabled: !timezoneProp });
  const timezone = timezoneProp || userTimezone;
  const isTimezoneMode = isTimezoneProp ?? (isTime || isTimeRange);
  const effectiveTimezone = isTimezoneMode ? timezone : null;

  // Single source of truth for the picker's state
  const [internalValue, setInternalValue] = useState(value);

  // Sync with external value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Derive date and time portions from internal value
  const dateValue = useMemo(
    () => extractDateValue(internalValue, isDateRange),
    [internalValue, isDateRange]
  );

  const timeValue = useMemo(
    () => extractTimeValue(internalValue, isTimeRange),
    [internalValue, isTimeRange]
  );

  const displayFormat = getDisplayFormat(format, value, isTime, isTimeRange);
  const buttonDisplayText = getButtonText({
    value,
    baseFormat: format,
    buttonText,
    isDate,
    isDateRange,
    isTime,
    isTimeRange,
    timezone: effectiveTimezone
  });

  // Simplified handlers - only update internalValue
  const handleDateChange = (newDateValue) => {
    const combined = combineDateTime(
      newDateValue,
      timeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const handleTimeChange = (newTimeValue) => {
    // For time range without date range, ensure we have a date (use today if none selected)
    const effectiveDateValue = dateValue || (isTimeRange && !isDateRange ? new Date() : null);
    const combined = combineDateTime(
      effectiveDateValue,
      newTimeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const handleCalendarChange = (calendarValue) => {
    const combined = combineDateTime(
      calendarValue,
      timeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const isRange = isDateRange || isTimeRange;

  const rangeError = useMemo(
    () => getRangeError(internalValue, isRange, effectiveTimezone),
    [internalValue, isRange, effectiveTimezone]
  );

  const isValid = Boolean(
    (Array.isArray(internalValue) ? internalValue[0] && internalValue[1] : internalValue) &&
    !rangeError
  );

  // Convert internal value to the output format expected by the consumer
  const convertOutput = (val) => {
    if (!val) return null;

    if (isDateOnly) {
      // Date-only strings pass through directly
      const dateOnly = typeof val === 'string' ? parseDateOnly(val) : null;
      if (dateOnly) return toDateOnlyString(dateOnly);

      // UTC ISO string: convert to timezone to get the correct calendar date
      if (typeof val === 'string' && /T/.test(val)) {
        const zoned = toZonedTime(new Date(val), timezone);
        return toDateOnlyString(zoned);
      }

      // Date object: use local fields directly
      if (val instanceof Date) return toDateOnlyString(val);

      return toDateOnlyString(new Date(val));
    }

    if (isTimezoneMode) {
      // Return UTC ISO string
      if (typeof val === 'string' && /T/.test(val)) return val;
      if (val instanceof Date) return val.toISOString();
      return new Date(val).toISOString();
    }

    // Default: return as-is for backward compat
    return val;
  };

  const onApply = (closePopover) => {
    if (!isValid) return;

    let output;
    if (isDateRange || isTimeRange) {
      output = Array.isArray(internalValue) ? internalValue.map(convertOutput) : internalValue;
    } else {
      output = convertOutput(internalValue);
    }

    onChange(output);
    closePopover();
  };

  const onCancel = (closePopover) => {
    // Reset to original value
    setInternalValue(value);
    closePopover();
  };

  const handleKeyDown = (event, closePopover) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isValid) onApply(closePopover);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel(closePopover);
    }
  };

  const showDateSelector = isDate || isDateRange;
  const showTimeSelector = isTime || isTimeRange;
  const calendarValue = dateValue || internalValue;

  return (
    <div className={cn('w-full', parentClassName)} data-field-error={error ? '' : undefined}>
      <Header label={label} required={required} />
      <Popover
        isFixed
        className="w-full"
        panelClassName={cn(
          'p-0 w-[368px] rounded-2xl border border-solid border-neutral-200 bg-white p-0 shadow-[0px_0px_20px_0px_rgba(55,65,69,0.08)]',
          // The popover caps panels at 70dvh and hides the overflow, which clipped the footer off the
          // bottom on short viewports. A date picker must never scroll — you cannot page through a
          // calendar — so raise the cap and, once even that cannot fit, centre the panel like a dialog.
          // `!` is required on the offsets: the popover positions itself with inline styles, and only
          // an important declaration outranks those.
          'max-h-[96dvh]',
          showTimeSelector ? CENTER_WHEN_SHORT_WITH_TIME : CENTER_WHEN_SHORT_DATE_ONLY,
          panelClassName
        )}
        customButtonClassName="w-full"
        customButtonTrigger={
          customButtonTrigger || (
            <OuterButton
              icon={buttonIcon}
              iconSize={buttonIconSize}
              iconClassName={buttonIconClassName}
              text={buttonDisplayText}
              textClassName={buttonTextClassName}
              className={buttonClassName}
              value={value}
            />
          )
        }>
        {({ closePopover }) => (
          <div
            onKeyDown={(e) => handleKeyDown(e, closePopover)}
            tabIndex={0}
            className="flex flex-col outline-none">
            <div
              className={cn(
                'flex flex-col gap-4 p-5',
                // Recover ~20px of vertical space on short viewports so the panel still fits whole
                // rather than clipping the footer. Same breakpoint as the centring above.
                COMPACT_WHEN_SHORT
              )}>
              {showDateSelector && (
                <DateSelector
                  isDate={isDate}
                  isDateRange={isDateRange}
                  isDateOnly={isDateOnly}
                  value={dateValue}
                  onChange={handleDateChange}
                  timezone={timezone}
                />
              )}
              <Calendar
                value={calendarValue}
                onChange={handleCalendarChange}
                isRange={isDateRange}
                format={displayFormat}
                minDate={minDate}
                maxDate={maxDate}
                firstDayOfWeek={firstDayOfWeek}
                timezone={timezone}
              />
              {showTimeSelector && (
                <TimeSelector
                  isTime={isTime}
                  isTimeRange={isTimeRange}
                  isDateRange={isDateRange}
                  value={timeValue}
                  onChange={handleTimeChange}
                  baseDate={dateValue}
                  showTimezone={showTimezone}
                  timezone={timezone}
                />
              )}
              {rangeError && (
                <p role="alert" className="text-sm text-danger-500">
                  {rangeError}
                </p>
              )}
            </div>
            <Footer
              onCancel={() => onCancel(closePopover)}
              onApply={() => onApply(closePopover)}
              isValid={isValid}
            />
          </div>
        )}
      </Popover>
      {error && <p className="pt-2 text-sm text-danger-500">{error}</p>}
    </div>
  );
};

export default Datepicker;
